/**
 * Core Canvas Function Utilities
 *
 * This file contains core canvas functions for zoom reset, text updates, and image addition
 * extracted from MyCanvas.js to improve code organization and maintainability.
 */

import { getUnsupportedFormatMessage } from "./imageFormatUtils";
import { stripBakedInCheckerboardFromImageBlob } from "./checkerboardAlphaUtils";
import {
  scheduleCenterScrollOnScaledCanvas,
  logCanvasHScroll,
} from "./zoomViewportUtils";

const addImagePasteDebug = (...args) => {
  try {
   

  } catch {
    /* noop */
  }
};

/**
 * Reset zoom to 100%
 * @param {Function} setZoomLevel - Setter for zoom level
 * @param {Function} setZoomInputValue - Setter for zoom input value
 * @param {Object} scrollAreaRef - Reference to scroll area
 * @param {Object} scaledContentRef - Reference to scaled canvas wrapper
 */
export const createResetZoom =
  (
    setZoomLevel,
    setZoomInputValue,
    scrollAreaRef,
    scaledContentRef,
    stageRef,
    mainStageContainerRef,
  ) =>
  () => {
    try {
      logCanvasHScroll("resetZoom", { to: 1 });
      setZoomLevel(1);
      setZoomInputValue(100);
      scheduleCenterScrollOnScaledCanvas(
        scrollAreaRef,
        scaledContentRef,
        stageRef,
        mainStageContainerRef,
      );
    } catch (error) {}
  };

/**
 * Update text element attributes
 * @param {string} id - Text element ID
 * @param {Object} newAttrs - New attributes to apply
 * @param {Array} texts - Current texts array
 * @param {Function} setTexts - Setter for texts array
 * @param {Function} saveToHistory - Function to save to history
 */
export const createUpdateText =
  (setTexts, saveToHistory) => (id, newAttrs, texts) => {
    setTexts(
      texts.map((text) => (text.id === id ? { ...text, ...newAttrs } : text))
    );
    saveToHistory();
  };

/**
 * Add image to canvas
 * @param {string} imageSrc - Image source URL or data URL
 * @param {string} referenceUrl - Reference URL for the image
 * @param {Blob} originalBlob - Original blob if available
 * @param {Function} setIsImageLoading - Setter for image loading state
 * @param {Function} setImageLoadingProgress - Setter for loading progress
 * @param {Function} setImages - Setter for images array
 * @param {Function} setSelectedObject - Setter for selected object
 * @param {Function} setSelectedObjects - Setter for selected objects array
 * @param {Function} setSelectedImage - Setter for selected image
 * @param {Function} setSelectedText - Setter for selected text
 * @param {Function} setSelectedTextId - Setter for selected text ID
 * @param {Function} setSelectedArrow - Setter for selected arrow
 * @param {Function} setSelectedLine - Setter for selected line
 * @param {Function} setSelectedDoubleSidedArrow - Setter for selected double-sided arrow
 * @param {Function} saveToHistory - Function to save to history
 * @param {Function} generateMarkerId - Function to generate unique IDs
 * @param {Function} compressImageIfNeeded - Function to compress images
 * @param {Function} processCompressedImage - Function to process compressed images
 * @param {Object} transformerRef - Reference to transformer
 * @param {Function} showError - Optional error callback
 * @param {Function|null} clampPastedImagePlacement - When `imageAddOptions.pasteTarget` is set, clamps center/size like pointerHandlers
 *
 * Returned addImage signature ends with optional `imageAddOptions`:
 * `{ isOnTopLayer?: boolean, stripCheckerboard?: boolean }` — defaults to true when omitted
 * for top layer; `stripCheckerboard` runs on blob/object-URL loads when true (paste / drop).
 */
export const createAddImage = (
  setIsImageLoading,
  setImageLoadingProgress,
  setImages,
  /** When `imageAddOptions.pasteTarget === 'off'`, appended to off-canvas images instead of main. */
  setOffCanvasImages,
  setSelectedObject,
  setSelectedObjects,
  setSelectedImage,
  setSelectedText,
  setSelectedTextId,
  setSelectedArrow,
  setSelectedLine,
  setSelectedDoubleSidedArrow,
  saveToHistory,
  generateMarkerId,
  compressImageIfNeeded,
  processCompressedImage,
  transformerRef,
  showError = null,
  clampPastedImagePlacement = null
) => {
  // Create a processing sources set to track duplicate processing
  const processingSources = new Set();
  let saveHistoryTimeout = null;

  return (
    imageSrc,
    referenceUrl = "",
    originalBlob = null,
    x = null,
    y = null,
    width = null,
    height = null,
    useTopLeftAnchor = false,
    imageAddOptions = null
  ) => {
    // Clear any existing saveToHistory timeout
    if (saveHistoryTimeout) {
      clearTimeout(saveHistoryTimeout);
      saveHistoryTimeout = null;
    }

    // Create a unique identifier for this image addition to prevent duplicates
    const uniqueId = `${imageSrc.substring(
      0,
      50
    )}_${Date.now()}_${Math.random()}`;

    /** Defer full-screen loader so tiny/fast images never flash the overlay. */
    let loaderDelayTimer = setTimeout(() => {
      setIsImageLoading(true);
      setImageLoadingProgress(10);
    }, 200);
    const clearLoaderDelay = () => {
      if (loaderDelayTimer != null) {
        clearTimeout(loaderDelayTimer);
        loaderDelayTimer = null;
      }
    };
    const showLoaderForHeavyWork = () => {
      clearLoaderDelay();
      setIsImageLoading(true);
      setImageLoadingProgress(12);
    };

    const img = new Image();
    const isRemoteHttpUrl = /^https?:\/\//i.test(imageSrc);
    if (isRemoteHttpUrl) {
      img.crossOrigin = "anonymous";
    }

    const SNAPPY_MAX_DIM = 1024;

    let uniqueSrc = imageSrc;
    if (imageSrc.startsWith("data:")) {
      const byteString = atob(imageSrc.split(",")[1]);
      const mimeString = imageSrc.split(",")[0].split(":")[1].split(";")[0];
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([ab], { type: mimeString });
      uniqueSrc = URL.createObjectURL(blob);
    } else if (
      imageSrc.startsWith("blob:") &&
      typeof window.createImageBitmap === "function"
    ) {
      // Off-thread decode: preserve original aspect ratio while downscaling.
      // Clipboard / pasted blobs are often PNG/WebP with alpha — JPEG + alpha:false
      // flattens transparency to black.
      uniqueSrc = imageSrc;
      fetch(imageSrc)
        .then((res) => res.blob())
        .then(async (blob) => {
          let b = blob;
          const wantStrip =
            imageAddOptions &&
            typeof imageAddOptions === "object" &&
            imageAddOptions.stripCheckerboard;
          addImagePasteDebug("blob pipeline", {
            wantStrip,
            fetchedType: blob?.type,
            fetchedSize: blob?.size,
            originalBlobType: originalBlob?.type,
          });
          if (wantStrip) {
            showLoaderForHeavyWork();
            try {
              b = await stripBakedInCheckerboardFromImageBlob(blob);
            } catch (err) {
              addImagePasteDebug("strip threw, using original blob", err);
              b = blob;
            }
          }
          // After checkerboard strip, b is often PNG with alpha — must not re-encode as JPEG.
          const mimeType =
            (b && b.type) || (originalBlob && originalBlob.type) || "";
          const encodeAsJpeg = /^image\/(jpeg|jpg)$/i.test(mimeType);
          addImagePasteDebug("after strip", {
            mimeType,
            encodeAsJpeg,
            blobReplaced: b !== blob,
            outSize: b?.size,
          });
          return createImageBitmap(b).then((bitmap) => ({
            bitmap,
            encodeAsJpeg,
          }));
        })
        .then(({ bitmap, encodeAsJpeg }) => {
          showLoaderForHeavyWork();
          setImageLoadingProgress(60);
          const bitmapWidth = bitmap.width;
          const bitmapHeight = bitmap.height;
          const scale = Math.min(
            1,
            SNAPPY_MAX_DIM / Math.max(bitmapWidth, bitmapHeight)
          );
          const targetWidth = Math.max(1, Math.round(bitmapWidth * scale));
          const targetHeight = Math.max(1, Math.round(bitmapHeight * scale));
          const canvas = document.createElement("canvas");
          canvas.width = targetWidth;
          canvas.height = targetHeight;
          const ctx = canvas.getContext("2d", {
            alpha: encodeAsJpeg ? false : true,
          });
          ctx.drawImage(bitmap, 0, 0, targetWidth, targetHeight);
          bitmap.close();
          const dataUrl = encodeAsJpeg
            ? canvas.toDataURL("image/jpeg", 0.72)
            : canvas.toDataURL("image/png");
          canvas.width = 0;
          canvas.height = 0;
          const compressedImg = new Image();
          compressedImg.crossOrigin = "anonymous";
          compressedImg.onload = () => {
            setImageLoadingProgress(70);
            processCompressedImageLocal(compressedImg, dataUrl);
          };
          compressedImg.onerror = () => {
            img.src = imageSrc;
          };
          compressedImg.src = dataUrl;
        })
        .catch((err) => {
          clearLoaderDelay();
          setIsImageLoading(false);
          setImageLoadingProgress(0);
          addImagePasteDebug("blob pipeline failed, falling back to direct img.src", err);
          img.src = imageSrc;
        });
    } else if (imageSrc.startsWith("blob:")) {
      addImagePasteDebug(
        "blob URL without createImageBitmap — checkerboard strip not run; use a modern browser"
      );
      uniqueSrc = imageSrc;
      img.src = imageSrc;
    } else {
      uniqueSrc = imageSrc + `#${Date.now()}`;
    }

    if (!imageSrc.startsWith("blob:")) {
      img.src = uniqueSrc;
    }

    img.onload = () => {
      clearLoaderDelay();
      setImageLoadingProgress(50);

      const originalWidth = img.naturalWidth;
      const originalHeight = img.naturalHeight;

      // Check if this exact image source is already being processed
      if (processingSources.has(imageSrc)) {
        setIsImageLoading(false);
        setImageLoadingProgress(0);
        return;
      }

      // Track this source as being processed
      processingSources.add(imageSrc);

      // Remove from processing set after a delay
      setTimeout(() => {
        processingSources.delete(imageSrc);
      }, 1000);

      setImageLoadingProgress(70);

      // Compress large images to prevent memory issues
      const compressedSrc = compressImageIfNeeded(img);

      // If compression happened, reload the compressed image
      if (compressedSrc !== imageSrc) {
        const compressedImg = new Image();
        compressedImg.crossOrigin = "anonymous";
        compressedImg.onload = () => {
          processCompressedImageLocal(compressedImg, compressedSrc);
        };
        compressedImg.onerror = () => {
          // If compressed version fails, use original
          processCompressedImageLocal(img, uniqueSrc);
        };
        compressedImg.src = compressedSrc;
      } else {
        processCompressedImageLocal(img, uniqueSrc);
      }
    };

    // Add error handler for image loading failures
    img.onerror = () => {
      clearLoaderDelay();
      // Remote URLs often fail with crossOrigin="anonymous" (no CORS headers) even though
      // the image is valid — retry without CORS so paste-from-URL (e.g. Google Images) works.
      const hadAnonymousCors =
        isRemoteHttpUrl &&
        (img.crossOrigin === "anonymous" || img.crossOrigin === "use-credentials");
      if (hadAnonymousCors) {
        try {
          img.crossOrigin = "";
          img.removeAttribute("crossorigin");
        } catch {
          /* ignore */
        }
        img.src = uniqueSrc;
        return;
      }

      setIsImageLoading(false);
      setImageLoadingProgress(0);

      // Try to determine the format from the image source
      let formatInfo = "";
      if (imageSrc.startsWith("data:")) {
        try {
          const mimeString = imageSrc.split(",")[0].split(":")[1].split(";")[0];
          formatInfo = mimeString;
        } catch (e) {
          formatInfo = "unknown";
        }
      } else if (originalBlob) {
        formatInfo = originalBlob.type || "unknown";
      }

      let errorMessage = formatInfo
        ? getUnsupportedFormatMessage(formatInfo, originalBlob?.name || "")
        : "Failed to load image. The image format may not be supported.";
      if (!formatInfo && isRemoteHttpUrl) {
        errorMessage =
          "Failed to load image from this URL. The link may be invalid, expired, or blocked.";
      }

      // Use showError callback if provided, otherwise fall back to alert
      if (showError && typeof showError === "function") {
        showError(errorMessage);
      } else {
        alert(errorMessage);
      }

      // Remove from processing set
      processingSources.delete(imageSrc);
    };

    // Local wrapper for processCompressedImage that handles state updates
    const processCompressedImageLocal = async (image, src) => {
      const processedData = await processCompressedImage(image, src);

      // DOWNSCALING DISABLED: Use full-resolution image so advanced crop modal is not blurry
      // (was: downscale large images to 2.5x display size for performance)
      let displayImage = processedData.compressedImage;
      // const naturalWidth = image.naturalWidth || image.width;
      // const naturalHeight = image.naturalHeight || image.height;
      // const displayWidth = width !== null && width !== undefined ? width : processedData.scaledWidth;
      // const displayHeight = height !== null && height !== undefined ? height : processedData.scaledHeight;
      // const scaleX = naturalWidth / displayWidth;
      // const scaleY = naturalHeight / displayHeight;
      // const maxScale = Math.max(scaleX, scaleY);
      // if (maxScale > 2) { ... downscale to targetWidth/targetHeight, displayImage = downscaledImage }

      const pasteTargetOff =
        imageAddOptions &&
        typeof imageAddOptions === "object" &&
        imageAddOptions.pasteTarget === "off" &&
        typeof setOffCanvasImages === "function";
      const setTarget = pasteTargetOff ? setOffCanvasImages : setImages;

      setTarget((prevImages) => {
        // Check if an image with the same uniqueId already exists
        const existingImage = prevImages.find(
          (img) => img.uniqueId === uniqueId
        );
        if (existingImage) {
          setIsImageLoading(false);
          setImageLoadingProgress(0);
          return prevImages;
        }

        const imageNumber = prevImages.length + 1;

        // CRITICAL: Log the dimensions being used to debug image upload dimension issues
        const finalWidth =
          width !== null && width !== undefined
            ? width
            : processedData.scaledWidth;
        const finalHeight =
          height !== null && height !== undefined
            ? height
            : processedData.scaledHeight;

        let placeX = x !== null ? x : 300;
        let placeY = y !== null ? y : 300;
        let outW = finalWidth;
        let outH = finalHeight;

        if (
          typeof clampPastedImagePlacement === "function" &&
          imageAddOptions &&
          (imageAddOptions.pasteTarget === "main" ||
            imageAddOptions.pasteTarget === "off") &&
          useTopLeftAnchor !== true
        ) {
          const placed = clampPastedImagePlacement({
            centerX: placeX,
            centerY: placeY,
            width: outW,
            height: outH,
            pasteTarget: imageAddOptions.pasteTarget,
          });
          if (placed) {
            placeX = placed.centerX;
            placeY = placed.centerY;
            outW = placed.width ?? outW;
            outH = placed.height ?? outH;
          }
        }

        const newImage = {
          id: generateMarkerId(),
          image: displayImage, // Full-resolution (downscaling disabled for crop modal clarity)
          x: placeX,
          y: placeY,
          width: outW,
          height: outH,
          src: src, // Keep src reference
          maintainAspectRatio: true,
          compress: true,
          // _originalImage: image, // (downscaling disabled)
          // _downscaledImage: displayImage !== image ? displayImage : undefined, // (downscaling disabled)
          imageNumber,
          name: `Picture ${imageNumber}`,
          referenceUrl,
          // Add unique identifier to prevent duplicates
          uniqueId: uniqueId,
          // Store the original aspect ratio for consistent resizing
          originalAspectRatio:
            outW > 0 && outH > 0
              ? outW / outH
              : processedData.scaledWidth / processedData.scaledHeight,
          // Store original dimensions for aspect ratio restoration
          originalWidth: processedData.scaledWidth,
          originalHeight: processedData.scaledHeight,
          isOnTopLayer:
            imageAddOptions &&
            typeof imageAddOptions === "object" &&
            Object.prototype.hasOwnProperty.call(
              imageAddOptions,
              "isOnTopLayer"
            )
              ? !!imageAddOptions.isOnTopLayer
              : true,
          // Use top-left anchor positioning like rectangles (for images drawn by user)
          useTopLeftAnchor: useTopLeftAnchor === true,
        };


        const updatedImages = [...prevImages, newImage];

        // Automatically select the newly created image after a short delay
        // to ensure the DOM node is rendered
        setTimeout(() => {
          // Clear all previous selections first
          setSelectedObject(null);
          setSelectedObjects([]);
          setSelectedImage(null);
          setSelectedText(null);
          setSelectedTextId(null);
          setSelectedArrow(null);
          setSelectedLine(null);
          setSelectedDoubleSidedArrow(null);

          // Clear transformer
          if (transformerRef.current) {
            transformerRef.current.nodes([]);
          }

          // Set the new image as selected
          setSelectedObject({
            ...newImage,
            type: "Image",
          });
          setSelectedImage(newImage);
        }, 50);

        // Save to history after state update with the correct images (prevent multiple calls)
        if (!saveHistoryTimeout) {
          saveHistoryTimeout = setTimeout(() => {
            if (pasteTargetOff) {
              saveToHistory(
                "add_image",
                null,
                null,
                null,
                null,
                null,
                null,
                null,
                null,
                null,
                null,
                null,
                null,
                null,
                null,
                { currentOffCanvasImagesOverride: updatedImages },
              );
            } else {
              saveToHistory(
                "add_image",
                updatedImages,
                null,
                null,
                null,
                null,
                null,
              );
            }
            // Clear the timeout reference
            saveHistoryTimeout = null;

            // Hide loading state
            setImageLoadingProgress(100);
            setTimeout(() => {
              setIsImageLoading(false);
              setImageLoadingProgress(0);
            }, 200);
          }, 100);
        }

        return updatedImages;
      });
    };
  };
};
